import React, { useContext, useMemo } from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Divider,
  Hidden,
  Stack,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import {
  formDataToFormikMapper,
  getLodgeErrorTypeEnum,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";
import axios from "axios";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  Form,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import FormContext from "src/context/FormContext";

import FormFieldLayout from "./FormComponents/FormFieldLayout";
import { AccountPayer, FormValues, Service } from "./types";
import { useNavigate } from "react-router-dom";
import {
  formValidationSchemaInternet,
  homeUserFormValSchemaInternet,
} from "./FormComponents/Validations";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import {
  getNestedValue,
  isDateWithinNext42Days,
  isUnavailableDateForFuture,
  isUnavailableDateForFuturePlusOne,
  showUpdatePremiseAlert,
} from "./helper";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import { useAppContext } from "src/context/AppContext";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { InputValueType } from "@origin-digital/ods-types/.";
import { usePermissions } from "src/hooks/usePermissions";
import InternetPreInstallBlock from "./InternetPreInstallBlock";
import { DatePickerWrapper } from "./FormComponents/DatePickerWrapper";
import { InternetRequestStatusEnum } from "./ServiceTypeStatusAlert";

const InternetBlock = (props: {
  setFormLoadValues: React.Dispatch<React.SetStateAction<FormValues>>;
  homeUser: boolean;
  premiseId: string | null;
}) => {
  const {
    context: { user },
  } = useAppContext();
  const navigate = useNavigate();
  const {
    formState: { internetService, isAccountReadOnly },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);

  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<FormValues>();
  const { setFormLoadValues, premiseId, homeUser } = props;
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();

  const isDigitalEic =
    homeUser ||
    (values.internetAccountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const showPreinstallationFields = Boolean(
    !!internetService?.connectionType &&
      (internetService?.connectionType === "PRE_INSTALL_NBN" ||
        internetService?.connectionType === "ADDRESS_NOT_FOUND")
  );
  const validationSchema = (readOnly?: boolean) =>
    homeUser
      ? homeUserFormValSchemaInternet(
          readOnly,
          isDigitalEic,
          showPreinstallationFields
        )
      : formValidationSchemaInternet(
          readOnly,
          isDigitalEic,
          showPreinstallationFields
        );

  // Keeping it commented for future reference
  const submitRequest = async () => {
    try {
      await apiClient.put(`/internet/status-update`, {
        request_id: internetService?.requestId,
        handover_date: values.internetHandoverDate,
      });
      // Call the new endpoint LOC-1350 to submit the request and get the updated request details in response

      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
        },
      });
    } catch (e: any) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(e) ?? null);
    }
  };

  const presubmissionRequestUpdate = async (isLodge: boolean) => {
    const manipulatedValues =
      isHomeUserFromPermissions &&
      values.accountHolder === "person" &&
      !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
      user
        ? {
            ...values,
            accountPrimaryEmail: user.email,
            accountPrimaryFirstName: user.first_name,
            accountPrimaryLastName: user.last_name,
          }
        : values;
    try {
      const updatePremiseResponse = await apiClient.post(
        "/draft/premise",
        premiseFormFieldMapper(
          manipulatedValues,
          {
            internetService: internetService ?? {},
          },
          {},
          false,
          undefined,
          isDigitalEic && isLodge,
          homeUser,
          isLodge
        )
      );

      const mappedFormikValues = formDataToFormikMapper(
        updatePremiseResponse.data
      );
      setFormLoadValues({
        ...mappedFormikValues,
        siteSupervisors: values.siteSupervisors,
      });

      const internetServiceResponse = updatePremiseResponse.data.services.find(
        (s: Service) => s.service_type === "INTERNET"
      );

      setFormState((prevState) => ({
        ...prevState,
        internetService: {
          ...prevState.internetService,
          draftId: internetServiceResponse?.draft_id,
          draftVersion: internetServiceResponse?.draft_version,
          requestId: internetServiceResponse?.request_id,
        },
      }));
      if (!showPreinstallationFields) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
          },
        });
      }
    } catch (e: any) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(e));
      throw e;
    }
  };

  const saveInternetBlock = async () => {
    try {
      await presubmissionRequestUpdate(false);
      setDisplayingSnackbar({
        message: "Internet block updated",
        tone: "positive",
      });
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save Internet block",
        tone: "caution",
      });
    }
  };

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "internetBlock",
    Boolean(premiseId)
  );
  const value: string | null =
    getNestedValue(values, "internetHandoverDate") ?? null;
  const preInstallValue: string | null =
    getNestedValue(values, "internetPreinstallationDate") ?? null;
  const errorInDt: string | undefined = getNestedValue(
    errors,
    "internetHandoverDate"
  );
  const helperText = errorInDt?.toString();
  const handleOnChange = React.useCallback(
    (date: Date | null) => {
      if (date != value) {
        setFieldValue("internetHandoverDate", date);
      }
    },
    [setFieldValue, value]
  );

  const handleOnChangePreInstallationDate = React.useCallback(
    (date: Date | null) => {
      if (date != preInstallValue) {
        setFieldValue("internetPreinstallationDate", date);
      }
    },
    [setFieldValue, preInstallValue]
  );
  const isLodgeInternet = Boolean(
    showPreinstallationFields
      ? internetService?.requestStatus === "IN_PROGRESS"
      : internetService?.handoverDate
  );
  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ||
      (!internetService &&
        values.internetRequestStatus ===
          InternetRequestStatusEnum.PRE_INSTALL_NBN) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          >
            {values.locId ? (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                <Column width="2/3">
                  {/* LOC ID */}
                  <Stack space="xsmall">
                    <Text variant="body-small" tone="neutralLight">
                      LOC ID
                    </Text>
                    <Text>{values.locId}</Text>
                    <Divider />
                  </Stack>
                </Column>
              </Columns>
            ) : null}
            {showPreinstallationFields && (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                <Column width="2/3">
                  <FormFieldLayout>
                    <DatePickerWrapper
                      id="internetPreinstallationDate"
                      label="Pre-installation date"
                      value={
                        values.internetPreinstallationDate
                          ? new Date(values.internetPreinstallationDate)
                          : null
                      }
                      onChange={handleOnChangePreInstallationDate}
                      shouldDisableDate={isUnavailableDateForFuturePlusOne}
                      helperText={errors.internetPreinstallationDate}
                      error={!!errors.internetPreinstallationDate}
                    />
                  </FormFieldLayout>
                  <Hidden below="lg">
                    <Column />
                  </Hidden>
                </Column>
                <Column width="1/3">
                  <Box display="flex" alignItems="stretch" height="full">
                    <Hidden below="md">
                      <Box
                        style={{ borderLeft: "1px solid #E3E3E3" }}
                        paddingRight="large"
                        height="full"
                      />
                    </Hidden>
                    <Box alignSelf="center" marginTop="xsmall">
                      <Stack>
                        <Text variant="body-small" tone="neutralLight">
                          As pre-installation is the installataion of the
                          connection box and fibre cable in an eligible
                          property. Fibre is extended from street into a home
                          while it's still being build.
                        </Text>
                        {/* If more paragraphs of <Text> required, add here */}
                      </Stack>
                    </Box>
                  </Box>
                </Column>
              </Columns>
            )}
            <Columns
              collapseBelow="md"
              space={{
                xs: "none",
                md: "xlarge",
              }}
              alignY="stretch"
            >
              <Column width="2/3">
                <FormFieldLayout>
                  <DatePickerWrapper
                    id="internetHandoverDate"
                    label="Handover date"
                    value={
                      internetService?.handoverDate
                        ? new Date(internetService.handoverDate)
                        : value || null
                    }
                    onChange={handleOnChange}
                    shouldDisableDate={
                      showPreinstallationFields
                        ? (date: Date) =>
                            isDateWithinNext42Days(
                              date,
                              values.internetPreinstallationDate ?? new Date()
                            ) //MM/DD/YYYY
                        : isUnavailableDateForFuture
                    }
                    helperText={helperText}
                    error={!!helperText}
                  />
                </FormFieldLayout>
                <Hidden below="lg">
                  <Column />
                </Hidden>
              </Column>
              <Column width="1/3">
                <Box display="flex" alignItems="stretch" height="full">
                  <Hidden below="md">
                    <Box
                      style={{ borderLeft: "1px solid #E3E3E3" }}
                      paddingRight="large"
                      height="full"
                    />
                  </Hidden>
                  <Box alignSelf="center" marginTop="xsmall">
                    <Stack>
                      <Text variant="body-small" tone="neutralLight">
                        The date you pick for handover needs to be minimum six
                        weeks after the pre‑installation date, which is the
                        required completion period for pre-installation.
                      </Text>
                      <Text variant="body-small" tone="neutralLight">
                        Once the request is lodged, you can change the handover
                        date if it’s still more than 30 days away. Once it’s
                        less than 30 days away, the date is locked and can’t be
                        changed.
                      </Text>
                      {/* If more paragraphs of <Text> required, add here */}
                    </Stack>
                  </Box>
                </Box>
              </Column>
            </Columns>
            {values.internetNbnAddress &&
              internetService?.connectionType === "CAN_RETAIL" && (
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "none",
                    md: "xlarge",
                  }}
                  alignY="stretch"
                >
                  <Column width="2/3">
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Internet connection address
                        </Text>
                        <Text>{values.internetNbnAddress}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </Column>
                  <Column width="1/3" />
                </Columns>
              )}
            {showPreinstallationFields && (
              <InternetPreInstallBlock homeUser={homeUser} />
            )}
            {values.internetAccountPayer === AccountPayer.SOMEONE_ELSE && (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                {/* Fields */}
                <Column width="2/3">
                  <Field type="checkbox" name="internetTerms">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<InputValueType>;
                      meta: FieldMetaProps<FormValues>;
                    }) => (
                      <CheckboxGroup
                        id="internetTermsGroup"
                        error={!!meta.error}
                        helperText={meta.error}
                      >
                        <Checkbox
                          {...field}
                          label={
                            <Text>
                              I have let the account holder know that their
                              information has been provided to Origin and told
                              them about Origin’s{" "}
                              <TextLink href="">
                                privacy and credit reporting statements
                              </TextLink>
                              .
                            </Text>
                          }
                          id="internetTerms"
                          checked={field.checked}
                          name="internetTerms"
                          onChange={(e) => {
                            setFieldValue("internetTerms", e.target.checked);
                          }}
                        />
                      </CheckboxGroup>
                    )}
                  </Field>
                </Column>
              </Columns>
            )}
            <PermissionsActionButtons
              primaryButtonProps={{
                onHandleClick: () =>
                  validationSchema(isAccountReadOnly)
                    .validate(values, {
                      abortEarly: false,
                      context: { homeUser },
                    })
                    .then(async () => {
                      return await presubmissionRequestUpdate(true);
                    })
                    .then(async () => {
                      if (showPreinstallationFields) {
                        // if preinstallation flow, call the /internet/status-update endpoint
                        return await submitRequest();
                      }
                    })
                    .catch((e) => {
                      if (axios.isAxiosError(e)) return;
                      setErrors(yupToFormErrors(e));
                    }),
              }}
              secondaryButtonProps={{
                onHandleClick: saveInternetBlock,
                disabled: internetService?.autoSubmit,
              }}
              isInternet={isLodgeInternet}
              showSecondaryButton={showPreinstallationFields}
            />
          </Stack>
        </Form>
      )}
    </>
  );
};

export default InternetBlock;
